import type { ComponentType } from 'react';

import {
  MARQUEE_EXTRAS,
  PROVIDER_BRAND_ICON,
  PROVIDER_DISPLAY_ORDER,
} from '@/components/icons/ProviderIcons';
import { PROVIDER_INFO } from '@/constants/providers';
import { cn } from '@/lib/utils';

type MarqueeIcon = ComponentType<{ className?: string; size?: number }>;

/**
 * Infinite provider-logo marquee. Two identical CSS-driven tracks; the seam
 * is invisible and prefers-reduced-motion stops the animation entirely
 * (handled in index.css).
 *
 * Rendered INSIDE the hero section so it sits above the fold on a typical
 * desktop viewport — it provides the social-proof beat of the first screen.
 */
export function Marquee() {
  // `id` (not `key`) so React's reserved `key` prop on the JSX call-site
  // doesn't collide with a spread of the same name.
  const marqueeItems = [
    ...PROVIDER_DISPLAY_ORDER.map((p) => ({
      id: p as string,
      label: PROVIDER_INFO[p].displayName,
      Icon: PROVIDER_BRAND_ICON[p] as MarqueeIcon,
    })),
    ...MARQUEE_EXTRAS.map((m) => ({
      id: m.key,
      label: m.label,
      Icon: m.Icon as MarqueeIcon,
    })),
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 sm:pb-14 lg:px-8">
      <p className="mb-6 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Tracks spend across the providers your team already uses
      </p>

      <div
        className="marquee"
        role="region"
        aria-label="Supported AI providers"
      >
        <div className="marquee-track" aria-hidden="false">
          {marqueeItems.map(({ id, label, Icon }) => (
            <MarqueeItem key={`a-${id}`} label={label} Icon={Icon} />
          ))}
        </div>
        <div className="marquee-track" aria-hidden="true">
          {marqueeItems.map(({ id, label, Icon }) => (
            <MarqueeItem key={`b-${id}`} label={label} Icon={Icon} />
          ))}
        </div>
      </div>
    </div>
  );
}

function MarqueeItem({ Icon, label }: { Icon: MarqueeIcon; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2.5 whitespace-nowrap text-muted-foreground/70 transition-colors duration-200 ease-out-quart hover:text-foreground',
      )}
      title={label}
    >
      <Icon className="size-7 shrink-0" />
      <span className="text-sm font-medium tracking-tight">{label}</span>
    </span>
  );
}
